// =====================================================================
//  TOOLS · shared primitives, physics + unit system
// ---------------------------------------------------------------------
//  Everything in this module is canonical SI internally.  The
//  `<UnitNumber>` input + `MultiResult` output components convert at the
//  surface so the user can think in inches, gpm, kPa, ft etc. without
//  the calculators carrying the conversion burden.
// =====================================================================

import React from "react";

// =====================================================================
//  REPORT BRANDING — client-side helpers for the per-tool "Report
//  branding" panel (Project name, Prepared for, Customer logo). PDF
//  rendering itself happens server-side via WeasyPrint (see
//  services/pdf_render.py::render_calculator_pdf) — this file just
//  captures the operator's branding intent and forwards it in the
//  export POST.
// =====================================================================

// Storage helpers — persist per-tool branding so operators don't retype
// the project name on every calculator. Wrapped in try/catch so we
// gracefully no-op in browsers where localStorage is disabled.
const brandingStorageKey = (slug) => `hv_report_branding:${slug || "global"}`;
function readBranding(slug) {
  try {
    const raw = localStorage.getItem(brandingStorageKey(slug));
    if (!raw) return { projectName: "", preparedFor: "", customerLogo: "" };
    const parsed = JSON.parse(raw);
    return {
      projectName: String(parsed.projectName || ""),
      preparedFor: String(parsed.preparedFor || ""),
      customerLogo: String(parsed.customerLogo || ""),  // base64 data URI
    };
  } catch {
    return { projectName: "", preparedFor: "", customerLogo: "" };
  }
}
function writeBranding(slug, value) {
  try {
    localStorage.setItem(brandingStorageKey(slug), JSON.stringify(value));
  } catch { /* private-mode / quota — silent no-op */ }
}

// Auto-downscale a user-uploaded logo to a max width in an off-screen
// <canvas> before we persist it. Keeps the localStorage entry (and
// the print HTML) small: a 5 MB PNG becomes ~40 KB.
function downscaleImageToDataUri(file, maxWidth = 400) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error("no file"));
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("invalid image"));
      img.onload = () => {
        const scale = img.width > maxWidth ? maxWidth / img.width : 1;
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        // PNG keeps transparency (customer logos on light headers).
        resolve(canvas.toDataURL("image/png"));
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });
}

// Hook — small state wrapper so ToolShell can render the panel
// declaratively. Reads once from localStorage on mount, then keeps
// storage in sync on every change.
function useReportBranding(slug) {
  const [branding, setBranding] = React.useState(() => readBranding(slug));
  React.useEffect(() => { setBranding(readBranding(slug)); }, [slug]);
  const update = React.useCallback((patch) => {
    setBranding((prev) => {
      const next = { ...prev, ...patch };
      writeBranding(slug, next);
      return next;
    });
  }, [slug]);
  return [branding, update];
}

// =====================================================================
//  PHYSICS — every formula is industry-standard, no approximations
// =====================================================================

// Hazen-Williams head loss in metres of water.
//   hL = 10.67 × L × (Q^1.852) / ( C^1.852 × D^4.87 )
//   Q in m³/s · D in m · L in m
export function hazenWilliamsHeadLoss({ flow_m3s, id_m, length_m, C }) {
  if (!flow_m3s || !id_m || !length_m || !C) return null;
  return (10.67 * length_m * Math.pow(flow_m3s, 1.852)) /
         (Math.pow(C, 1.852) * Math.pow(id_m, 4.87));
}

// Velocity:  V = Q / A , A = π D² / 4    (Q in m³/s, D in m → V in m/s)
export function pipeVelocity({ flow_m3s, id_m }) {
  if (!flow_m3s || !id_m) return null;
  const A = Math.PI * id_m * id_m / 4;
  return flow_m3s / A;
}

// NPSH available (all in metres of water column).
//   NPSHa = (Pₐ − Pᵥ) / (ρg) ± static head − friction loss
export function npshAvailable({ atm_m, vap_m, static_m, friction_m }) {
  return atm_m - vap_m + static_m - friction_m;
}

// TDS ↔ EC (factor depends on dominant salt mix)
export function ecToTds(ec_uScm, factor) { return ec_uScm * factor; }
export function tdsToEc(tds_mgL, factor) { return tds_mgL / factor; }

// Electrical
export function ampsToKw({ amps, volts, pf, threePhase }) {
  if (!amps || !volts) return null;
  const k = threePhase ? Math.sqrt(3) : 1;
  return (k * volts * amps * pf) / 1000;
}
export function kwToAmps({ kw, volts, pf, threePhase }) {
  if (!kw || !volts) return null;
  const k = threePhase ? Math.sqrt(3) : 1;
  return (kw * 1000) / (k * volts * pf);
}

// Rainwater yield in litres = m² × mm × runoff coefficient
export function rainwaterYield({ area_m2, rain_mm, coeff }) {
  if (!area_m2 || !rain_mm) return null;
  return area_m2 * rain_mm * coeff;
}

export function storageSizing({ persons, lpd, days }) {
  if (!persons || !lpd || !days) return null;
  const dailyL = persons * lpd;
  return { dailyL, reserveL: dailyL * days };
}

// =====================================================================
//  UNIT SYSTEM — factors to SI base unit per dimension
// =====================================================================
//   Each entry: { factor, label }.  to_si(value, unit) = value * factor
//   Length base = m, Flow base = m³/s, Pressure base = bar (we display
//   the most pump-room-relevant unit as base), Volume base = L,
//   Area base = m², Rainfall base = mm, Velocity base = m/s,
//   Conductivity base = µS/cm, TDS base = mg/L.
export const UNITS = {
  length_small: {
    base: "mm",
    factors: { mm: 1, cm: 10, m: 1000, inch: 25.4 },
    labels:  { mm: "mm", cm: "cm", m: "m", inch: "inch" },
  },
  length_big: {
    base: "m",
    factors: { m: 1, ft: 0.3048, km: 1000, mi: 1609.34 },
    labels:  { m: "m", ft: "ft", km: "km", mi: "mi" },
  },
  flow: {
    // base = m³/s
    base: "Lps",
    factors: {
      Lps:  0.001,                  // 1 L/s = 0.001 m³/s
      Lpm:  0.001 / 60,             // 1 L/min = 1.6667e-5 m³/s
      m3h:  1 / 3600,               // 1 m³/h
      m3d:  1 / 86400,              // 1 m³/day
      gpm:  3.7854 / 60 / 1000,     // US gpm to m³/s
    },
    labels: { Lps: "L/s", Lpm: "L/min", m3h: "m³/h", m3d: "m³/day", gpm: "US gpm" },
  },
  pressure: {
    // base = bar
    base: "bar",
    factors: {
      bar:   1,
      kPa:   0.01,
      psi:   0.0689476,
      mH2O:  0.0980665,
    },
    labels: { bar: "bar", kPa: "kPa", psi: "psi", mH2O: "m H₂O" },
  },
  velocity: {
    base: "mps",
    factors: { mps: 1, fps: 0.3048 },
    labels:  { mps: "m/s", fps: "ft/s" },
  },
  volume: {
    base: "L",
    factors: {
      L: 1,
      kL: 1000,
      m3: 1000,
      USgal: 3.78541,
    },
    labels: { L: "L", kL: "kL", m3: "m³", USgal: "US gal" },
  },
  area: {
    base: "m2",
    factors: { m2: 1, ft2: 0.092903, ha: 10000 },
    labels:  { m2: "m²", ft2: "ft²", ha: "ha" },
  },
  rainfall: {
    base: "mm",
    factors: { mm: 1, inch: 25.4 },
    labels:  { mm: "mm", inch: "inch" },
  },
  conductivity: {
    base: "uScm",
    factors: { uScm: 1, mScm: 1000 },
    labels:  { uScm: "µS/cm", mScm: "mS/cm" },
  },
  tds: {
    base: "mgL",
    factors: { mgL: 1, ppm: 1, gL: 1000 },   // mg/L == ppm for dilute water
    labels:  { mgL: "mg/L", ppm: "ppm", gL: "g/L" },
  },
};

// Convert a value FROM display unit TO the dimension's base.
export function toBase(dim, value, unit) {
  const def = UNITS[dim];
  if (!def) return value;
  const f = def.factors[unit];
  return value * (f ?? 1);
}
// Convert FROM the base TO a display unit.
export function fromBase(dim, valueInBase, unit) {
  const def = UNITS[dim];
  if (!def) return valueInBase;
  const f = def.factors[unit];
  return valueInBase / (f ?? 1);
}
// Convert between two display units in a single dimension.
export function convert(dim, value, fromUnit, toUnit) {
  const inBase = toBase(dim, value, fromUnit);
  return fromBase(dim, inBase, toUnit);
}

// =====================================================================
//  PALETTE — locked to the site's hybrid system
// =====================================================================
export const colors = {
  navy:       "#0C2A40",
  navyDeep:   "#0A1A28",
  cyan:       "#1FB6C1",
  paper:      "#FAFAF7",
  paperWarm:  "#F4F2EC",
  ink:        "#1A1F25",
};

// =====================================================================
//  SHARED UI primitives
// =====================================================================
export function Field({ label, hint, children, span = 1 }) {
  return (
    <label className={`block ${span === 2 ? "md:col-span-2" : ""}`}>
      <div
        className="font-mono mb-1.5"
        style={{
          fontSize: "0.6rem",
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          color: "rgba(12,42,64,0.7)",
        }}
      >
        {label}
      </div>
      {children}
      {hint && (
        <div
          className="mt-1.5 font-light"
          style={{ fontSize: "0.74rem", color: "rgba(12,42,64,0.55)", lineHeight: 1.45 }}
        >
          {hint}
        </div>
      )}
    </label>
  );
}

// Number input with a unit selector at the end of the row.
// **Decimal-safe**: stores the raw text in a local buffer so React doesn't
// strip trailing dots ("2.") or leading zeros ("0.5") mid-typing. The
// parent gets the parsed number via onChange only when it parses cleanly;
// an empty / dangling input pushes 0 so downstream calcs don't NaN out.
export function UnitNumber({ value, onChange, dim, unit, onUnitChange, units, testid }) {
  const def = UNITS[dim];
  const list = units || (def ? Object.keys(def.factors) : []);
  const numeric = Number(value);
  const externalStr = Number.isFinite(numeric) && numeric !== 0 ? String(numeric) : "";
  const [buf, setBuf] = React.useState(externalStr);
  // Sync the buffer with external state when the parent resets the value
  // for reasons unrelated to typing (preset selection, reset button…).
  // We compare PARSED equality so the user's in-flight "2." doesn't get
  // wiped by a numerically-identical 2 from the parent.
  React.useEffect(() => {
    const parsed = parseFloat(buf.replace(",", "."));
    const same = Number.isFinite(parsed)
      ? Math.abs(parsed - numeric) < 1e-9
      : (buf === "" && numeric === 0);
    if (!same) setBuf(externalStr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  if (!def) return null;
  return (
    <div className="flex items-stretch">
      <input
        type="text"
        inputMode="decimal"
        value={buf}
        placeholder="0"
        onChange={(e) => {
          const raw = e.target.value;
          // Accept both "." and "," — SA decimal separator is comma.
          if (raw !== "" && !/^-?\d*[.,]?\d*$/.test(raw)) return;
          setBuf(raw);
          if (raw === "" || raw === "-" || raw === "." || raw === ",") { onChange(0); return; }
          const n = parseFloat(raw.replace(",", "."));
          if (Number.isFinite(n)) onChange(n);
        }}
        data-testid={testid}
        className="flex-1 min-w-0 px-3 py-2.5 bg-white border outline-none focus:border-[#1FB6C1] transition-colors"
        style={{
          borderColor: "rgba(12,42,64,0.16)",
          color: colors.navy,
          fontSize: "0.95rem",
          fontWeight: 500,
        }}
      />
      <select
        value={unit}
        onChange={(e) => onUnitChange(e.target.value)}
        data-testid={testid ? `${testid}-unit` : undefined}
        className="px-2 border border-l-0 bg-[#FAFAF7] outline-none focus:border-[#1FB6C1]"
        style={{
          borderColor: "rgba(12,42,64,0.16)",
          color: "rgba(12,42,64,0.78)",
          fontSize: "0.75rem",
          letterSpacing: "0.04em",
          minWidth: 78,
        }}
      >
        {list.map((u) => (
          <option key={u} value={u}>{def.labels[u]}</option>
        ))}
      </select>
    </div>
  );
}

// ─── SuffixNum — decimal-safe number input with a fixed-text suffix ──
// Shared replacement for the per-calculator SuffixNum copies. Stores
// the raw text in a local buffer so React doesn't eat trailing dots
// ("2.") or leading zeros ("0.5") mid-typing. The parent gets the
// parsed number via onChange; an empty / dangling input pushes 0.
//
// Decimals are allowed on EVERY field — even ones where decimals are
// nonsensical (e.g. 0.5 days) — because the user asked for consistent
// UX across the whole calculator suite and gating per-field is fiddly.
export function SuffixNum({ value, onChange, suffix, testid, placeholder = "0" }) {
  const numeric = Number(value);
  const externalStr = Number.isFinite(numeric) && numeric !== 0 ? String(numeric) : "";
  const [buf, setBuf] = React.useState(externalStr);
  React.useEffect(() => {
    const parsed = parseFloat(buf.replace(",", "."));
    const same = Number.isFinite(parsed)
      ? Math.abs(parsed - numeric) < 1e-9
      : (buf === "" && numeric === 0);
    if (!same) setBuf(externalStr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <div className="flex items-stretch">
      <input
        type="text"
        inputMode="decimal"
        value={buf}
        placeholder={placeholder}
        onChange={(e) => {
          const raw = e.target.value;
          // Accept both "." and "," as decimal separators (SA-locale
          // keyboards default to comma). The buffer preserves whatever
          // the user typed; parseFloat normalizes for the parent.
          if (raw !== "" && !/^-?\d*[.,]?\d*$/.test(raw)) return;
          setBuf(raw);
          if (raw === "" || raw === "-" || raw === "." || raw === ",") { onChange(0); return; }
          const n = parseFloat(raw.replace(",", "."));
          if (Number.isFinite(n)) onChange(n);
        }}
        data-testid={testid}
        className="flex-1 min-w-0 px-3 py-2.5 bg-white border outline-none focus:border-[#1FB6C1] transition-colors"
        style={{
          borderColor: "rgba(12,42,64,0.16)",
          color: colors.navy,
          fontSize: "0.95rem",
          fontWeight: 500,
        }}
      />
      {suffix !== undefined && suffix !== null && suffix !== "" && (
        <span
          className="inline-flex items-center px-3 border border-l-0 font-mono"
          style={{
            background: "#FAFAF7",
            borderColor: "rgba(12,42,64,0.16)",
            color: "rgba(12,42,64,0.65)",
            fontSize: "0.72rem",
          }}
        >
          {suffix}
        </span>
      )}
    </div>
  );
}


export function Select({ value, onChange, options, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full px-3 py-2.5 bg-white border outline-none focus:border-[#1FB6C1] transition-colors"
      style={{
        borderColor: "rgba(12,42,64,0.16)",
        color: colors.navy,
        fontSize: "0.92rem",
        fontWeight: 500,
      }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

// Compact result tile that can show ONE primary unit + a row of equivalents
// underneath.  `primary` is the main displayed value/unit.  `alts` is a list
// of {value, unit} pairs shown smaller below.
export function Result({ label, primary, alts, status, note, testid }) {
  const ring = status === "alert" ? "#B85C38"
             : status === "warn"  ? "#C2922E"
             : colors.cyan;
  return (
    <div
      className="p-5"
      style={{
        background: "#FFFFFF",
        borderLeft: `3px solid ${ring}`,
        border: "1px solid rgba(12,42,64,0.08)",
        borderRadius: 10,
        boxShadow: "0 1px 2px rgba(12,42,64,0.04), 0 8px 22px -14px rgba(12,42,64,0.10)",
      }}
      data-testid={testid}
    >
      <div
        className="font-mono mb-1.5"
        style={{
          fontSize: "0.6rem",
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: "rgba(12,42,64,0.6)",
        }}
      >
        {label}
      </div>
      <div className="flex items-baseline gap-2 flex-wrap">
        <span
          className="font-display"
          style={{
            fontSize: "1.85rem",
            fontWeight: 500,
            color: colors.navy,
            letterSpacing: "-0.015em",
            lineHeight: 1,
          }}
        >
          {primary.value}
        </span>
        <span
          className="font-mono"
          style={{
            fontSize: "0.74rem",
            color: "rgba(12,42,64,0.65)",
            letterSpacing: "0.04em",
          }}
        >
          {primary.unit}
        </span>
      </div>
      {alts && alts.length > 0 && (
        <div
          className="mt-2 font-mono"
          style={{ fontSize: "0.72rem", color: "rgba(12,42,64,0.55)", letterSpacing: "0.02em" }}
        >
          ={" "}
          {alts.map((a, i) => (
            <React.Fragment key={i}>
              <span style={{ color: "rgba(12,42,64,0.7)" }}>{a.value}</span>{" "}
              <span>{a.unit}</span>
              {i < alts.length - 1 && <span className="mx-1.5" style={{ color: "rgba(12,42,64,0.3)" }}>·</span>}
            </React.Fragment>
          ))}
        </div>
      )}
      {note && (
        <div
          className="mt-2.5 font-light leading-relaxed"
          style={{
            fontSize: "0.8rem",
            color: status ? ring : "rgba(12,42,64,0.7)",
          }}
        >
          {note}
        </div>
      )}
    </div>
  );
}

// =====================================================================
//  PDF EXPORT — opens a clean print window using the same approach as
//  the Terms & Conditions PDF.  Each calculator passes a small report
//  payload via the onExport callback.
//    payload = {
//      title, oneLiner,
//      inputs:  [{ label, value, unit }]
//      results: [{ label, value, unit, alts:[{value,unit}], note, status }]
//      notes:   ['optional commentary paragraphs']
//    }
// =====================================================================
function esc(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export function printCalculationReport(payload) {
  const { title, branding = {} } = payload;
  // POST the payload to the WeasyPrint backend, download the returned PDF.
  //
  // Rationale for backend rendering: the previous client-side approach
  // (window.print of a Blob URL, then position:fixed / thead-tfoot repeats)
  // had two Chromium bugs we could not work around — the source URL was
  // printed in Chrome's native page footer, and <tfoot> did not anchor to
  // the page bottom on partial-content pages, so multi-page reports had a
  // mid-page floating footer. WeasyPrint supports CSS Paged Media
  // margin boxes (@top-left / @bottom-left / @bottom-right) which anchor
  // reliably on every page. Same code path in every browser.
  const API_URL =
    (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) ||
    (typeof window !== "undefined" && window.__REACT_APP_BACKEND_URL) ||
    "";
  const filename = `HidroVerse — ${title} — report.pdf`.replace(/[\\/]/g, "-");

  fetch(`${API_URL}/api/tools/calc-pdf`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...payload, branding }),
  })
    .then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.blob();
    })
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      // Trigger a download AND open a print preview in a new tab so the
      // operator can either save or send-to-printer immediately.
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.rel = "noopener";
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke the object URL after a delay so any concurrent tab-open
      // (via middle-click on the download link) still has a valid URL.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    })
    .catch((err) => {
      // eslint-disable-next-line no-alert
      alert(
        "PDF export failed. Please try again — if the issue persists, contact HidroVerse support.\n\nDetails: " +
          (err && err.message ? err.message : String(err))
      );
    });
}

// Wrapper for an individual calculator card inside a group.
export function ToolShell({ slug, title, oneLiner, onExport, children }) {
  const [branding, updateBranding] = useReportBranding(slug);
  const [brandingOpen, setBrandingOpen] = React.useState(false);
  return (
    <article
      id={slug}
      className="scroll-mt-28 overflow-hidden"
      data-testid={`tool-${slug}`}
      style={{
        background: "#FFFFFF",
        border: "1px solid rgba(12,42,64,0.08)",
        borderRadius: 14,
        boxShadow: "0 1px 2px rgba(12,42,64,0.04), 0 18px 36px -20px rgba(12,42,64,0.16)",
      }}
    >
      <header
        className="px-4 sm:px-6 md:px-8 py-5 border-b flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-4"
        style={{ borderColor: "rgba(12,42,64,0.08)", background: colors.paperWarm }}
      >
        <div className="flex-1 min-w-0">
          <h4
            className="font-display"
            style={{
              fontSize: "clamp(1.05rem, 1.6vw, 1.25rem)",
              fontWeight: 600,
              color: colors.navy,
              letterSpacing: "-0.005em",
            }}
          >
            {title}
          </h4>
          {oneLiner && (
            <p
              className="mt-1 font-light"
              style={{ fontSize: "0.85rem", color: "rgba(12,42,64,0.68)", maxWidth: "70ch" }}
            >
              {oneLiner}
            </p>
          )}
        </div>
        {onExport && (
          <div className="shrink-0 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setBrandingOpen((v) => !v)}
              className="inline-flex items-center gap-2 px-3 py-2 font-mono uppercase transition-colors"
              style={{
                fontSize: "0.62rem",
                letterSpacing: "0.18em",
                color: brandingOpen ? colors.cyan : colors.navy,
                border: `1px solid ${brandingOpen ? colors.cyan : "rgba(12,42,64,0.18)"}`,
                background: brandingOpen ? "rgba(31,182,193,0.06)" : "#FFFFFF",
                borderRadius: 999,
              }}
              data-testid={`tool-${slug}-branding-toggle`}
              title="Add a project name, logo and 'prepared for' line to this PDF"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M4 4h16v4H4z" />
                <path d="M4 12h10" />
                <path d="M4 18h16" />
              </svg>
              {branding.projectName || branding.customerLogo || branding.preparedFor
                ? "Report branding ●" : "Report branding"}
            </button>
            <button
              type="button"
              onClick={() => {
                const data = onExport();
                if (data) printCalculationReport({ title, oneLiner, ...data, branding });
              }}
              className="inline-flex items-center gap-2 px-3 py-2 font-mono uppercase transition-colors"
              style={{
                fontSize: "0.62rem",
                letterSpacing: "0.18em",
                color: colors.navy,
                border: "1px solid rgba(12,42,64,0.18)",
                background: "#FFFFFF",
                borderRadius: 999,
              }}
              data-testid={`tool-${slug}-export`}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = colors.cyan;
                e.currentTarget.style.color = colors.cyan;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(12,42,64,0.18)";
                e.currentTarget.style.color = colors.navy;
              }}
              title="Export this calculation as PDF"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Export PDF
            </button>
          </div>
        )}
      </header>
      {onExport && brandingOpen && (
        <ReportBrandingPanel
          slug={slug}
          branding={branding}
          onChange={updateBranding}
        />
      )}
      <div className="p-4 sm:p-6 md:p-7">{children}</div>
    </article>
  );
}

// -----------------------------------------------------------------------
//  Report branding panel — the slim accordion under each ToolShell
//  header. Three inputs: project name, prepared-for line, customer
//  logo (auto-downscaled to max 400px in an off-screen canvas so the
//  base64 data URI we persist stays small). Everything lives in
//  localStorage per-tool via slug; nothing hits the network.
// -----------------------------------------------------------------------
function ReportBrandingPanel({ slug, branding, onChange }) {
  const fileRef = React.useRef(null);
  const [error, setError] = React.useState("");
  const onFile = async (e) => {
    setError("");
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp|svg\+xml|gif)$/i.test(file.type)) {
      setError("Please pick a PNG, JPG, WEBP or SVG image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Image is over 5 MB — pick a smaller one.");
      return;
    }
    try {
      const dataUri = await downscaleImageToDataUri(file, 400);
      onChange({ customerLogo: dataUri });
    } catch {
      setError("Could not read that image. Try a different file.");
    }
  };
  return (
    <div
      className="px-4 sm:px-6 md:px-8 py-4 border-b"
      style={{ borderColor: "rgba(12,42,64,0.08)", background: "#FCFCFA" }}
      data-testid={`tool-${slug}-branding-panel`}
    >
      <div className="font-mono uppercase mb-3"
        style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.6)" }}>
        Report branding · optional
      </div>
      <p className="font-light mb-3" style={{ fontSize: "0.78rem", color: "rgba(12,42,64,0.62)", lineHeight: 1.5 }}>
        Prints alongside the HidroVerse header on the exported PDF. All fields are
        optional and remembered on this device until you clear them.
      </p>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Project name" hint="Shown top-right on the PDF header.">
          <input
            type="text"
            value={branding.projectName}
            onChange={(e) => onChange({ projectName: e.target.value })}
            placeholder='e.g. "Thornybush RO upgrade — Phase 2"'
            data-testid={`tool-${slug}-branding-project`}
            className="w-full px-3 py-2.5 bg-white border outline-none focus:border-[#1FB6C1]"
            style={{
              borderColor: "rgba(12,42,64,0.16)",
              color: colors.navy,
              fontSize: "0.95rem",
              fontWeight: 500,
            }}
          />
        </Field>
        <Field label="Prepared for" hint="e.g. client contact name or company.">
          <input
            type="text"
            value={branding.preparedFor}
            onChange={(e) => onChange({ preparedFor: e.target.value })}
            placeholder='e.g. "General Manager · Thornybush Game Lodge"'
            data-testid={`tool-${slug}-branding-prepared`}
            className="w-full px-3 py-2.5 bg-white border outline-none focus:border-[#1FB6C1]"
            style={{
              borderColor: "rgba(12,42,64,0.16)",
              color: colors.navy,
              fontSize: "0.95rem",
              fontWeight: 500,
            }}
          />
        </Field>
        <Field label="Customer logo" span={2}
          hint="PNG / JPG / SVG · max 5 MB. Auto-downscaled to ≈ 2 cm on the PDF.">
          <div className="flex items-center gap-3 flex-wrap">
            {branding.customerLogo ? (
              <img
                src={branding.customerLogo}
                alt="Customer logo preview"
                data-testid={`tool-${slug}-branding-logo-preview`}
                style={{
                  maxHeight: 44, maxWidth: 88, width: "auto", height: "auto",
                  padding: 6, background: "#FFFFFF",
                  border: "1px solid rgba(12,42,64,0.16)",
                }}
              />
            ) : (
              <div
                style={{
                  width: 88, height: 44, background: "#FFFFFF",
                  border: "1px dashed rgba(12,42,64,0.22)",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  fontSize: "0.65rem", color: "rgba(12,42,64,0.45)",
                  letterSpacing: "0.14em", textTransform: "uppercase",
                }}
              >
                No logo
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
              onChange={onFile}
              data-testid={`tool-${slug}-branding-logo-input`}
              style={{ display: "none" }}
            />
            <button
              type="button"
              onClick={() => fileRef.current && fileRef.current.click()}
              className="px-3 py-2 font-mono uppercase"
              style={{
                fontSize: "0.62rem", letterSpacing: "0.18em",
                color: colors.navy, background: "#FFFFFF",
                border: "1px solid rgba(12,42,64,0.18)",
                borderRadius: 999, cursor: "pointer",
              }}
              data-testid={`tool-${slug}-branding-logo-pick`}
            >
              {branding.customerLogo ? "Replace logo" : "Upload logo"}
            </button>
            {branding.customerLogo && (
              <button
                type="button"
                onClick={() => { onChange({ customerLogo: "" }); if (fileRef.current) fileRef.current.value = ""; }}
                className="px-3 py-2 font-mono uppercase"
                style={{
                  fontSize: "0.62rem", letterSpacing: "0.18em",
                  color: "rgba(12,42,64,0.6)", background: "transparent",
                  border: "1px solid rgba(12,42,64,0.14)",
                  borderRadius: 999, cursor: "pointer",
                }}
                data-testid={`tool-${slug}-branding-logo-clear`}
              >
                Remove
              </button>
            )}
          </div>
          {error && (
            <div className="mt-2 font-light" style={{ fontSize: "0.78rem", color: "#B85C38" }}>
              {error}
            </div>
          )}
        </Field>
      </div>
    </div>
  );
}

export function fmt(n, digits = 2) {
  if (n === null || n === undefined || Number.isNaN(n) || !Number.isFinite(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 10000) return Number(n.toFixed(0)).toLocaleString();
  if (abs >= 100)   return Number(n.toFixed(0)).toLocaleString();
  if (abs >= 10)    return Number(n.toFixed(1)).toLocaleString();
  if (abs >= 1)     return Number(n.toFixed(2)).toLocaleString();
  return Number(n.toFixed(digits + 1)).toLocaleString();
}

// =====================================================================
//  PIPE FITTINGS LIBRARY
// ---------------------------------------------------------------------
//  Industry-standard equivalent-length ratios (L/D) for common fittings.
//  Source: Crane Technical Paper TP-410 / Cameron Hydraulic Data — the
//  same values Grundfos, Wilo, DAB publish in their pump-selection
//  manuals.  Used by adding   Σ qty × (L/D) × ID   to the straight pipe
//  length, then computing friction via Hazen-Williams.  Accurate
//  within ~ ±15 % for water at typical pump-room velocities.
// =====================================================================
export const FITTINGS = [
  // --- bends & turns ---
  { id: "elbow-90-lr",   group: "Bends & turns",   label: "90° elbow (long radius)",          LD: 20  },
  { id: "elbow-90-sr",   group: "Bends & turns",   label: "90° elbow (short radius / threaded)", LD: 30  },
  { id: "elbow-45",      group: "Bends & turns",   label: "45° elbow",                          LD: 16  },
  { id: "swan-neck",     group: "Bends & turns",   label: "Swan-neck / 180° return bend",       LD: 50  },
  // --- tees, branches, joins ---
  { id: "tee-run",       group: "Tees & joins",    label: "Tee — flow through run (straight)", LD: 20  },
  { id: "tee-branch",    group: "Tees & joins",    label: "Tee — flow into branch (turn)",     LD: 60  },
  { id: "union",         group: "Tees & joins",    label: "Union / coupling (smooth bore)",    LD: 3   },
  { id: "reducer",       group: "Tees & joins",    label: "Reducer / enlargement (gradual)",   LD: 5   },
  // --- entries & exits ---
  { id: "pipe-entry",    group: "Entries & exits", label: "Pipe entrance from tank (sharp)",   LD: 25  },
  { id: "pipe-exit",     group: "Entries & exits", label: "Pipe exit into tank",                LD: 50  },
  { id: "strainer-y",    group: "Entries & exits", label: "Y-type strainer (clean)",           LD: 50  },
  // --- valves ---
  { id: "gate-open",     group: "Valves",          label: "Gate valve (fully open)",           LD: 8   },
  { id: "ball-open",     group: "Valves",          label: "Ball valve, full bore (open)",      LD: 3   },
  { id: "butterfly-open",group: "Valves",          label: "Butterfly valve (open)",            LD: 45  },
  { id: "globe-open",    group: "Valves",          label: "Globe valve (open)",                LD: 340 },
  { id: "diaphragm-open",group: "Valves",          label: "Diaphragm valve (open)",            LD: 130 },
  { id: "check-swing",   group: "Valves",          label: "Check / non-return valve (swing)",  LD: 100 },
  { id: "foot-hinge",    group: "Valves",          label: "Foot valve, hinged disc + strainer", LD: 75  },
  { id: "foot-poppet",   group: "Valves",          label: "Foot valve, poppet disc + strainer", LD: 420 },
];

// Σ qty × L/D × ID  →  equivalent length in metres.
export function fittingsEquivalentLength(qtyMap, id_m) {
  if (!qtyMap || !id_m) return 0;
  let total = 0;
  for (const f of FITTINGS) {
    const q = qtyMap[f.id] || 0;
    if (q > 0) total += q * f.LD * id_m;
  }
  return total;
}

// Human-readable summary of selected fittings (for PDF export).
export function fittingsSummary(qtyMap) {
  if (!qtyMap) return [];
  return FITTINGS
    .filter((f) => (qtyMap[f.id] || 0) > 0)
    .map((f) => ({ label: f.label, qty: qtyMap[f.id], LD: f.LD }));
}

// =====================================================================
//  FittingsPicker — collapsible "Advanced" panel that maps fittings
//  quantities → equivalent length given the current pipe ID.
// =====================================================================
export function FittingsPicker({ qtyMap, onChange, id_m, testid }) {
  const [open, setOpen] = React.useState(false);

  // Group fittings for cleaner layout.
  const grouped = React.useMemo(() => {
    const g = {};
    for (const f of FITTINGS) {
      (g[f.group] = g[f.group] || []).push(f);
    }
    return g;
  }, []);

  const totalEqLen = fittingsEquivalentLength(qtyMap, id_m);
  const totalCount = Object.values(qtyMap || {}).reduce((a, b) => a + (Number(b) || 0), 0);

  const setQty = (id, q) => {
    const next = { ...qtyMap };
    if (q <= 0) delete next[id]; else next[id] = q;
    onChange(next);
  };
  const clear = () => onChange({});

  return (
    <div
      className="md:col-span-2"
      data-testid={testid}
      style={{ border: "1px solid rgba(12,42,64,0.10)", background: "#FFFFFF" }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 transition-colors"
        style={{
          background: open ? colors.paperWarm : "#FFFFFF",
          borderBottom: open ? "1px solid rgba(12,42,64,0.08)" : "none",
        }}
        data-testid={testid ? `${testid}-toggle` : undefined}
      >
        <div className="flex items-center gap-3">
          <span
            className="font-mono uppercase"
            style={{ fontSize: "0.62rem", letterSpacing: "0.22em", color: colors.cyan, fontWeight: 700 }}
          >
            Advanced
          </span>
          <span className="font-display" style={{ fontSize: "0.95rem", color: colors.navy, fontWeight: 600 }}>
            Pipe fittings
          </span>
          <span className="font-mono"
            style={{ fontSize: "0.7rem", color: "rgba(12,42,64,0.55)", letterSpacing: "0.04em" }}>
            {totalCount > 0
              ? `${totalCount} fitting${totalCount === 1 ? "" : "s"} · adds ${fmt(totalEqLen, 1)} m equiv. length`
              : "Add elbows, valves, tees, fittings"}
          </span>
        </div>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden
          style={{ transform: open ? "rotate(180deg)" : "rotate(0)", transition: "transform 200ms", color: colors.navy, opacity: 0.55 }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="p-4 md:p-5">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5">
            {Object.entries(grouped).map(([groupName, items]) => (
              <div key={groupName}>
                <div
                  className="font-mono uppercase mb-2 mt-2 first:mt-0"
                  style={{ fontSize: "0.58rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.55)" }}
                >
                  {groupName}
                </div>
                <ul role="list" className="space-y-1">
                  {items.map((f) => {
                    const q = qtyMap?.[f.id] || 0;
                    return (
                      <li key={f.id} className="flex items-center justify-between py-1">
                        <span className="flex-1 min-w-0 pr-2" title={f.label}
                          style={{ fontSize: "0.82rem", color: colors.navy, lineHeight: 1.3 }}>
                          {f.label}
                          <span
                            className="ml-1.5 font-mono"
                            style={{ fontSize: "0.68rem", color: "rgba(12,42,64,0.45)", letterSpacing: "0.02em" }}>
                            L/D&nbsp;{f.LD}
                          </span>
                        </span>
                        <div className="flex items-stretch shrink-0 overflow-hidden" style={{ border: "1px solid rgba(12,42,64,0.16)", borderRadius: 999 }}>
                          <button type="button" onClick={() => setQty(f.id, Math.max(0, q - 1))}
                            className="px-2 py-1 hover:bg-[#FAFAF7]"
                            data-testid={testid ? `${testid}-${f.id}-minus` : undefined}
                            style={{ color: q > 0 ? colors.navy : "rgba(12,42,64,0.3)", fontSize: "0.85rem", lineHeight: 1 }}
                            aria-label={`Decrease ${f.label}`}>−</button>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={q === 0 ? "" : String(q)}
                            onChange={(e) => {
                              const raw = e.target.value;
                              // Accept "." or "," (SA decimal separator).
                              if (raw !== "" && !/^-?\d*[.,]?\d*$/.test(raw)) return;
                              if (raw === "" || raw === "-" || raw === "." || raw === ",") {
                                setQty(f.id, 0);
                                return;
                              }
                              const v = parseFloat(raw.replace(",", "."));
                              setQty(f.id, Number.isFinite(v) && v > 0 ? v : 0);
                            }}
                            onFocus={(e) => e.target.select()}
                            data-testid={testid ? `${testid}-${f.id}-qty` : undefined}
                            aria-label={`${f.label} quantity`}
                            className="px-1 text-center outline-none focus:bg-[#FAFAF7]"
                            style={{
                              width: 38,
                              fontSize: "0.85rem",
                              color: q > 0 ? colors.navy : "rgba(12,42,64,0.4)",
                              fontWeight: 600,
                              borderLeft: "1px solid rgba(12,42,64,0.10)",
                              borderRight: "1px solid rgba(12,42,64,0.10)",
                              background: "transparent",
                              MozAppearance: "textfield",
                            }}
                          />
                          <button type="button" onClick={() => setQty(f.id, q + 1)}
                            className="px-2 py-1 hover:bg-[#FAFAF7]"
                            data-testid={testid ? `${testid}-${f.id}-plus` : undefined}
                            style={{ color: colors.navy, fontSize: "0.85rem", lineHeight: 1 }}
                            aria-label={`Increase ${f.label}`}>+</button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>

          {totalCount > 0 && (
            <div
              className="mt-4 pt-3 flex items-center justify-between"
              style={{ borderTop: "1px solid rgba(12,42,64,0.08)" }}
            >
              <div className="font-mono" style={{ fontSize: "0.72rem", color: "rgba(12,42,64,0.65)", letterSpacing: "0.02em" }}>
                Total equivalent length added:
                <span style={{ color: colors.navy, fontWeight: 700, marginLeft: 8 }}>
                  {fmt(totalEqLen, 2)} m
                </span>
              </div>
              <button type="button" onClick={clear}
                className="font-mono uppercase px-3 py-1 hv-link"
                style={{ fontSize: "0.6rem", letterSpacing: "0.18em", color: colors.cyan, border: "1px solid rgba(31,182,193,0.35)" }}
                data-testid={testid ? `${testid}-clear` : undefined}>
                Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Format helper for an alts row: build {value, unit} for a number plus
// a list of unit codes in a given dimension.
export function altsFor(dim, valueInBase, unitCodes, digits = 2) {
  return unitCodes.map((u) => ({
    value: fmt(fromBase(dim, valueInBase, u), digits),
    unit: UNITS[dim].labels[u],
  }));
}


// =====================================================================
//  SHARED VISUAL BLOCKS — used across calculators for visual consistency
//  between web + PDF outputs. Both are intentionally small and have
//  zero internal state. Add them at the bottom of any tool's output.
// =====================================================================

/**
 * SpecCard — the navy "supplier-ready specification" block.
 * Renders a single bold sentence summarising the calculator's headline
 * output, with cyan accent strong-tags around the numbers that matter.
 *
 *   <SpecCard
 *     eyebrow="Supplier-ready specification"
 *     testid="friction-spec-card"
 *   >
 *     Specify a pipe rated for <strong>{fmt(flow,2)} m³/h</strong>
 *     with friction loss of <strong>{fmt(hl,2)} m H₂O</strong>.
 *   </SpecCard>
 */
export function SpecCard({ eyebrow = "Specification", children, footer, testid }) {
  return (
    <div
      className="mt-5 p-5"
      style={{ background: "#0C2A40", color: "#F5F7FA" }}
      data-testid={testid}
    >
      <div
        className="font-mono uppercase mb-3"
        style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "#1FB6C1", fontWeight: 700 }}
      >
        {eyebrow}
      </div>
      <div style={{ fontSize: "0.95rem", lineHeight: 1.65 }}>
        {children}
      </div>
      {footer && (
        <div
          className="mt-3 font-light"
          style={{ fontSize: "0.78rem", color: "rgba(245,247,250,0.7)", lineHeight: 1.55 }}
        >
          {footer}
        </div>
      )}
    </div>
  );
}

/**
 * SpecValue — convenience wrapper for the cyan accent number inside a
 * SpecCard. Equivalent to <strong style={{color:"#1FB6C1"}}>...</strong>.
 */
export function SpecValue({ children }) {
  return <strong style={{ color: "#1FB6C1", fontWeight: 700 }}>{children}</strong>;
}

/**
 * FormulaSummary — the cyan "A + B = C at conditions" inline summary.
 * Used to compress a multi-input calculation into a one-line equation
 * the user can read at a glance.
 *
 *   <FormulaSummary
 *     label="Total dynamic head"
 *     parts={[
 *       { value: 20, unit: "m static" },
 *       { op: "+" },
 *       { value: 5.1, unit: "m friction" },
 *     ]}
 *     equals={{ value: 25.1, unit: "m TDH" }}
 *     trailing="at design flow 12 m³/h"
 *     testid="friction-summary"
 *   />
 */
export function FormulaSummary({ label, parts = [], equals, trailing, tone = "cyan", testid }) {
  const palette = tone === "amber"
    ? { bg: "#FFFBF2", border: "rgba(194,146,46,0.30)" }
    : tone === "warn"
      ? { bg: "#FFF5EE", border: "rgba(184,92,56,0.30)" }
      : { bg: "#F3FBFB", border: "rgba(31,182,193,0.30)" };

  return (
    <div
      className="mt-4 px-5 py-4 flex flex-wrap items-baseline gap-x-2 gap-y-1"
      style={{ background: palette.bg, border: `1px solid ${palette.border}`, borderRadius: 10 }}
      data-testid={testid}
    >
      <div
        className="font-mono uppercase mr-3"
        style={{ fontSize: "0.58rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.55)" }}
      >
        {label}
      </div>
      {parts.map((p, i) =>
        p.op
          ? <span key={i} className="font-mono" style={{ fontSize: "0.85rem", color: "rgba(12,42,64,0.55)" }}>{p.op}</span>
          : (
            <span key={i} style={{ fontSize: "0.92rem", color: "#0C2A40" }}>
              {p.value} {p.unit && (
                <span className="font-mono" style={{ fontSize: "0.72rem", color: "rgba(12,42,64,0.55)" }}>
                  {p.unit}
                </span>
              )}
            </span>
          )
      )}
      {equals && (
        <>
          <span className="font-mono" style={{ fontSize: "0.85rem", color: "rgba(12,42,64,0.55)" }}>=</span>
          <span style={{ fontSize: "1.2rem", fontWeight: 700, color: "#0C2A40" }}>
            {equals.value}{" "}
            {equals.unit && (
              <span className="font-mono" style={{ fontSize: "0.72rem", color: "rgba(12,42,64,0.55)" }}>
                {equals.unit}
              </span>
            )}
          </span>
        </>
      )}
      {trailing && (
        <span
          className="ml-auto font-mono"
          style={{ fontSize: "0.72rem", color: "rgba(12,42,64,0.55)" }}
        >
          {trailing}
        </span>
      )}
    </div>
  );
}
